import { createSelector } from "@reduxjs/toolkit";
import { fullNameFromParts } from "helpers/name-helpers";
import { RootState } from "redux/store";
import RequestStatus from "redux/slices/RequestStatus";
import {
  CancerStageCount,
  Case,
  CaseExport,
  CaseInfo,
  CaseReviewView,
  DocumentStatus,
  KeyValue,
  LensCaseFindings,
  NamedFirm,
  SectionValueView,
} from "generated/graphql";
import { KeyedCaseValueCalc } from "Backend";
import { type DocumentIntakeAugmented } from "./case.slice";
import { SectionOption } from "components/SectionSelect";

export const selectCase = (state: RootState): Case => state.case.case;
export const selectCaseStatus = (state: RootState): RequestStatus => state.case.status;
export const selectCaseInfo = (state: RootState): CaseInfo => state.case.case.info;
export const selectSectionValues = (state: RootState): SectionValueView[] => state.case.sectionValues;

export const selectCaseClaimantName = createSelector([selectCase], (lensCase) => {
  const givenName = lensCase.info.claimantGivenName;
  const surname = lensCase.info.claimantSurname;

  return fullNameFromParts(givenName, surname);
});

export const selectCaseClientStatus = createSelector([selectCase], (lensCase) => {
  return lensCase.clientStatus;
});

export const selectCaseProperties = (state: RootState): KeyValue[] => state.case.case.properties;
export const selectCaseCalcs = (state: RootState): KeyedCaseValueCalc[] => state.case.calcs;
export const selectCaseReviews = (state: RootState): CaseReviewView[] => state.case.reviews;

export const selectCaseInsightsPending = (state: RootState): boolean => state.case.caseInsightsPending;

// intake queries
function statusSortValue(status: DocumentStatus): number {
  return status === DocumentStatus.PendingReview
    ? 0
    : status === DocumentStatus.Processing
      ? 1
      : status === DocumentStatus.Reviewed
        ? 2
        : 3;
}

export const selectIntakes = (state: RootState): DocumentIntakeAugmented[] => state.case.intakes;
export const selectIntakesStatus = (state: RootState): RequestStatus => state.case.status;
export const selectIntakeById =
  (intakeId: number | undefined) =>
  (state: RootState): DocumentIntakeAugmented | undefined => {
    return state.case.intakes.find((i) => i.id === intakeId);
  };
export const selectIntakeByDocumentSegmentId =
  (documentSegmentId: number | undefined) =>
  (state: RootState): DocumentIntakeAugmented | undefined => {
    return state.case.intakes.find((i) => i.documentSegments.find((ds) => ds.id === documentSegmentId));
  };
export const selectIntakesSortedByStatus = createSelector([selectIntakes], (intakes) =>
  intakes.slice(0).sort((a, b) => statusSortValue(a.status) - statusSortValue(b.status))
);

// export queries
export const selectCaseExports = (state: RootState): CaseExport[] => state.case.exports;
export const selectCaseExportsStatus = (state: RootState): RequestStatus => state.case.exportsStatus;
export const selectDeselectedExportTypes = (state: RootState): string[] => state.case.deselectedExportTypes;
export const selectFilteredCaseExports = createSelector(
  [selectCaseExports, selectDeselectedExportTypes],
  (exports, deselectedExportTypes) => {
    const deselectedExportTypesSet = new Set(deselectedExportTypes);
    return exports.filter((e) => !deselectedExportTypesSet.has(e.exportType));
  }
);
export const selectUniqueExportTypes = createSelector([selectCaseExports], (exports) =>
  Array.from(new Set(exports.map((e) => e.exportType)))
);

// review queries
export const selectReviewBaseFindings = (state: RootState): LensCaseFindings | undefined => state.case.baseFindings;

export const selectReviewFilteredFindingsStatus = (state: RootState): RequestStatus =>
  state.case.filteredFindingsStatus;
export const selectReviewBaseFindingsStatus = (state: RootState): RequestStatus => state.case.baseFindingsStatus;
export const selectReviewCurrentFindings = (state: RootState): LensCaseFindings | undefined =>
  state.case.filteredFindings;

export const selectReviewCurrentCancerStageOptions = (state: RootState): CancerStageCount[] => {
  return state.case.baseFindings?.totalCancerStageCounts || [];
};

export const selectReviewCurrentCancerRecurrenceYesCount = (state: RootState): number => {
  return state.case.baseFindings?.totalCancerRecurrenceCounts.yes || 0;
};

export const selectSectionOptions = (state: RootState): SectionOption[] => {
  const clusterTypeCounts = state.case.baseFindings?.totalSectionCounts.clusterTypes;
  const clusterTypeCountOptions: SectionOption[] = (clusterTypeCounts || []).map((c) => ({
    key: c.clusterType,
    type: "cluster_type",
    count: c.count,
  }));
  const pageTypeCounts = state.case.baseFindings?.totalSectionCounts.pageTypes;
  const pageTypeCountOptions: SectionOption[] = (pageTypeCounts || []).map((p) => ({
    key: p.pageType,
    type: "page_type",
    count: p.count,
  }));
  const pageLabelCounts = state.case.baseFindings?.totalSectionCounts.labels;
  const pageLabelCountOptions: SectionOption[] = (pageLabelCounts || []).map((l) => ({
    key: l.labelName,
    type: "label",
    count: l.count,
  }));

  const sectionOptions: SectionOption[] = clusterTypeCountOptions
    .concat(pageTypeCountOptions)
    .concat(pageLabelCountOptions)
    .filter((o) => o.key !== "cover");
  return sectionOptions;
};

export const selectCaseCollaboratorFirms = (state: RootState): NamedFirm[] => state.case.collaboratorFirms;
